import {Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {HttpStatusCode} from '@angular/common/http';
import {interval, Subscription} from 'rxjs';
import {AutoCompleteCompleteEvent, AutoCompleteOnSelectEvent} from 'primeng/autocomplete';
import {CustomMessageService, OriginMessageError, ResponseHttpError, ResponseHttpSuccess, UtilService} from 'library';
import {RequestService} from 'projects/store/src/app/services/api/v1/request.service';
import {UserValidatorService} from 'projects/store/src/app/services/api/v1/user-validator.service';
import {NotificationOtpService} from 'projects/store/src/app/services/api/v1/notification-otp.service';
import {SalesWorkflow} from "projects/store/src/app/enums";

/**
 * Componente principal de Verification Code
 * Contiene toda la lógica común para V2 y V3
 * Actúa como container que renderiza la versión correcta según el flujo
 */
@Component({
  selector: 'app-sales-verification-code',
  templateUrl: './sales-verification-code.component.html',
})
export class SalesVerificationCodeComponent implements OnDestroy {
  // FormGroups
  formEmail!: FormGroup;
  formCode!: FormGroup;

  // Email autocomplete properties
  commonDomains = ['gmail.com', 'hotmail.com', 'outlook.com', 'yahoo.com', 'icloud.com'];
  filteredDomains: string[] = [];

  // State properties - Email
  requestId: string;
  flowCode: string;
  isEmailSubmitted = false;
  loadingSendCode = false;
  emailSent = false;
  isSendingCode = false;
  visibleConfirmationModal = false;

  // State properties - OTP
  loadingValidateCode = false;
  codeSubmitted = false;
  isValidatingCode = false;
  canResendCode = false;

  // OTP Service properties
  otpServicesOptions: any[] = [];
  selectedOtpService: string = '';
  messageOtpService: string = '';
  showOtpServiceModal = false;

  // Timer properties
  timeNewCodeSending: { minutes: number; seconds: number } = { minutes: 0, seconds: 0 };
  maxTimeToRetry: { minutes: number; seconds: number } = { minutes: 2, seconds: 0 };
  finishDate!: Date;
  timerInterval?: Subscription;

  // ViewChild for code inputs
  @ViewChild('firstNumber') firstNumberInput!: ElementRef;
  @ViewChild('secondNumber') secondNumberInput!: ElementRef;
  @ViewChild('thirdNumber') thirdNumberInput!: ElementRef;
  @ViewChild('fourthNumber') fourthNumberInput!: ElementRef;

  // Control del modal
  showModal: boolean = false;
  modalType: 'success' | 'error' = 'success';
  buttonIcon: string = 'pi pi-refresh';
  modalTitle: string = '';
  modalMessage: string = '';
  modalButtonLabel: string = 'Continuar';
  lastFailedOperation: 'loadOtpOptions' | 'sendCode' | 'resendCode' | 'validateCode' | 'saveEmail' | null = null;

  @Output() nextStepEvent = new EventEmitter();

  constructor(
    protected requestService: RequestService,
    protected userValidatorService: UserValidatorService,
    protected utilService: UtilService,
    protected route: ActivatedRoute,
    protected customMessageService: CustomMessageService,
    protected notificationOtpService: NotificationOtpService
  ) {
    this.requestId = this.route.snapshot.params['request_id'];
    this.flowCode = this.route.snapshot.params['flow_code'];
  }

  /**
   * Determina si el flujo actual es Krdya (V3)
   */
  get isFlowKrdya(): boolean {
    return this.flowCode === SalesWorkflow['app-krdya-cliente'] || this.flowCode === SalesWorkflow['app-krdya-kredicash'];
  }

  ngOnInit() {
    // Container: la inicialización se delega a los hijos (V2/V3)
  }

  ngOnDestroy() {
    if (this.timerInterval) {
      this.timerInterval.unsubscribe();
    }
  }

  initEmailForm() {
    this.formEmail = new FormGroup({
      email: new FormControl(null, {
        validators: [Validators.required, Validators.email],
         asyncValidators: [
           this.userValidatorService.emailServiceExistValidator(),
           this.userValidatorService.emailServiceStatusValid()
         ],
        updateOn: 'blur'
      })
    });
  }

  initCodeForm() {
    this.formCode = new FormGroup({
      firstNumber: new FormControl('', [Validators.required, Validators.pattern(/^[0-9]$/), Validators.maxLength(1)]),
      secondNumber: new FormControl('', [Validators.required, Validators.pattern(/^[0-9]$/), Validators.maxLength(1)]),
      thirdNumber: new FormControl('', [Validators.required, Validators.pattern(/^[0-9]$/), Validators.maxLength(1)]),
      fourthNumber: new FormControl('', [Validators.required, Validators.pattern(/^[0-9]$/), Validators.maxLength(1)])
    });
  }

  onDomainFilter(event: AutoCompleteCompleteEvent) {
    const query = event.query;

    if (query.includes('@')) {
      const [prefix, partialDomain] = query.split('@');
      this.filteredDomains = this.commonDomains
        .filter((domain) => domain.startsWith(partialDomain.toLowerCase()))
        .map((domain) => `${prefix}@${domain}`);
    } else {
      this.filteredDomains = [];
    }
  }

  onDomainSelect(event: AutoCompleteOnSelectEvent) {
    this.formEmail.patchValue({email: event.value});
  }

  normalizeEmail(email: string): string {
    return email.trim().toLowerCase();
  }

  onSendCode() {
    this.isEmailSubmitted = true;

    if (this.formEmail.invalid) {
      this.formEmail.markAllAsTouched();
      return;
    }

    // Prevenir doble clic (idempotencia)
    if (this.isSendingCode) {
      return;
    }

    this.sendVerificationCode(false);
  }

  // OTP Methods
  //Consultar las opciones de OTP disponibles
  showGetOtpOptions() {
    this.notificationOtpService.get().subscribe({
      next: (resp: any) => {
        this.otpServicesOptions = resp;
        this.selectedOtpService = this.requestService.request['person']?.otp_service ?? this.otpServicesOptions[0]?.value ?? 'whatsapp';
        this.messageOtpService = this.notificationOtpService.getDetailOtp(this.selectedOtpService).message;
      },
      error: (error: ResponseHttpError) => {
        this.utilService.disablePageLoading();
        this.customMessageService.responseNoticeHandler(error, OriginMessageError.BACKEND);
      }
    });
  }

  startTimer() {
    this.canResendCode = false;
    this.timeNewCodeSending = { ...this.maxTimeToRetry };
    this.finishDate = new Date();
    this.finishDate.setTime(
      this.finishDate.getTime() +
      (this.timeNewCodeSending.minutes * 60 + this.timeNewCodeSending.seconds) * 1000
    );

    if (this.timerInterval) {
      this.timerInterval.unsubscribe();
    }

    this.timerInterval = interval(1000).subscribe(() => {
      this.updateTime();
    });
  }

  updateTime() {
    const now = new Date();
    const diff = this.finishDate.getTime() - now.getTime();

    const mins = Math.floor(diff / (1000 * 60));
    const secs = Math.floor(diff / 1000);

    if (mins <= 0 && secs <= 0) {
      this.timeNewCodeSending = { minutes: 0, seconds: 0 };
      this.canResendCode = true;
      if (this.timerInterval) {
        this.timerInterval.unsubscribe();
      }
    } else {
      this.timeNewCodeSending = {
        minutes: mins,
        seconds: secs - mins * 60
      };
    }
  }

  getCodeValue(): string {
    return (
      this.formCode.get('firstNumber')?.value +
      this.formCode.get('secondNumber')?.value +
      this.formCode.get('thirdNumber')?.value +
      this.formCode.get('fourthNumber')?.value
    );
  }

  onValidateCode() {
    this.codeSubmitted = true;

    if (this.formCode.invalid) {
      this.formCode.markAllAsTouched();
      return;
    }

    // Prevenir doble clic (idempotencia)
    if (this.isValidatingCode) {
      return;
    }

    this.isValidatingCode = true;
    this.loadingValidateCode = true;

    const code = this.getCodeValue();

    this.requestService.checkEmailVerificationCode(this.requestId, code).subscribe({
      next: (resp: ResponseHttpSuccess) => {
        if (resp.status === HttpStatusCode.Ok) {
          this.visibleConfirmationModal = false;
          this.utilService.enablePageLoading();
          this.saveEmailAndAdvance();
        }
      },
      error: (error: ResponseHttpError) => {
        this.loadingValidateCode = false;
        this.isValidatingCode = false;

        if (error.status === HttpStatusCode.NotAcceptable) {
          this.formCode.setErrors({ invalid: true });
        } else {
          this.customMessageService.responseNoticeHandler(error, OriginMessageError.BACKEND);
        }
      }
    });
  }

  onResendCode() {
    if (!this.canResendCode) {
      return;
    }
    this.sendVerificationCode(true);
  }

  sendVerificationCode(isResend: boolean) {
    this.isSendingCode = true;
    this.loadingSendCode = true;

    const email = this.normalizeEmail(this.formEmail.get('email')?.value);

    this.requestService.sendEmailVerificationCode(this.requestId, email, isResend).subscribe({
      next: (resp: ResponseHttpSuccess) => {
        if (resp.status === HttpStatusCode.Ok) {
          if (isResend) {
            this.resetCodeForm();
          } else {
            this.emailSent = true;
            this.visibleConfirmationModal = true;
            // Esperar a que el DOM se renderice antes de hacer focus
            setTimeout(() => {
              if (this.firstNumberInput) {
                this.firstNumberInput.nativeElement.focus();
              }
            }, 0);
          }
          this.startTimer();
        }
        this.loadingSendCode = false;
        this.isSendingCode = false;
      },
      error: (error: ResponseHttpError) => {
        this.loadingSendCode = false;
        this.isSendingCode = false;
        this.customMessageService.responseNoticeHandler(error, OriginMessageError.BACKEND);
      }
    });
  }

  onCodeInput(event: any, nextInput?: HTMLInputElement) {
    const value = event.target.value;

    // Solo permitir números
    if (value && !/^[0-9]$/.test(value)) {
      event.target.value = '';
      return;
    }

    // Auto-focus al siguiente input si hay valor
    if (value && nextInput) {
      nextInput.focus();
    }
  }

  onCodeKeydown(event: KeyboardEvent, prevInput?: HTMLInputElement) {
    const target = event.target as HTMLInputElement;

    // Permitir retroceder con backspace cuando el input está vacío
    if (event.key === 'Backspace' && !target.value && prevInput) {
      prevInput.focus();
    }
  }

  resetCodeForm() {
    this.formCode.reset();
    this.codeSubmitted = false;
    if (this.firstNumberInput) {
      this.firstNumberInput.nativeElement.focus();
    }
  }

  saveEmailAndAdvance() {
    const email = this.normalizeEmail(this.formEmail.get('email')?.value);

    this.requestService.saveAdditionalData(this.requestId, { id: 'email', value: email }).subscribe({
      next: (resp: ResponseHttpSuccess) => {
        if (resp.status === HttpStatusCode.Ok || resp.status === HttpStatusCode.NoContent) {
          this.onNextStep();
        }

        this.loadingValidateCode = false;
        this.isValidatingCode = false;
      },
      error: (error: ResponseHttpError) => {
        this.loadingValidateCode = false;
        this.isValidatingCode = false;
        this.customMessageService.responseNoticeHandler(error, OriginMessageError.BACKEND);
      }
    });
  }

  onNextStep() {
    this.nextStepEvent.emit();
  }
}
